import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { brl, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Calendar, Users, Trophy, Copy, Timer, CheckCircle2, Plus, Minus, PlayCircle } from "lucide-react";

const QUICK = [5, 10, 25, 50, 100, 500];

export default function RaffleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [raffle, setRaffle] = useState(null);
  const [qty, setQty] = useState(10);
  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [countdown, setCountdown] = useState(900);
  const [useCredit, setUseCredit] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const load = () => api.get(`/raffles/${id}`).then(({ data }) => setRaffle(data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (user) api.get("/affiliate/me").then(({ data }) => setCreditBalance(data?.credit_balance || 0)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!open || !order) return;
    setCountdown(900);
    const t = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [open, order]);

  // Poll Mercado Pago for payment confirmation while drawer is open
  useEffect(() => {
    if (!open || !order || order.status === "paid") return;
    let cancelled = false;
    const poll = async () => {
      try {
        setCheckingPayment(true);
        const { data } = await api.get(`/purchases/${order.order_id}/status`);
        if (cancelled) return;
        if (data.status === "paid") {
          toast.success("Pagamento confirmado! 🎉 Suas cotas foram garantidas.");
          setOpen(false);
          setOrder(null);
          await load();
          navigate("/dashboard");
        } else {
          setOrder((prev) => prev ? { ...prev, status: data.status } : prev);
        }
      } catch { /* ignore */ }
      finally { setCheckingPayment(false); }
    };
    const iv = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [open, order?.order_id, order?.status]);

  const pct = useMemo(() => raffle ? Math.min(100, Math.round((raffle.sold_quotas / raffle.total_quotas) * 100)) : 0, [raffle]);
  const total = useMemo(() => raffle ? qty * raffle.quota_price : 0, [qty, raffle]);

  const buy = async () => {
    if (!user) {
      toast.error("Faça login para comprar cotas");
      navigate("/login");
      return;
    }
    try {
      const { data } = await api.post("/purchases", { raffle_id: id, quantity: qty, use_credit: useCredit });
      setOrder(data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar pedido");
    }
  };

  const confirmPayment = async () => {
    if (!order) return;
    setConfirming(true);
    try {
      await new Promise((r) => setTimeout(r, 1200));
      await api.post("/purchases/confirm", { order_id: order.order_id });
      toast.success("Pagamento confirmado! 🎉 Suas cotas estão garantidas.");
      setOpen(false);
      setOrder(null);
      await load();
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao confirmar");
    } finally { setConfirming(false); }
  };

  if (!raffle) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-400">Carregando...</div>;

  const mins = String(Math.floor(countdown / 60)).padStart(2, "0");
  const secs = String(countdown % 60).padStart(2, "0");

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="card-luxury rounded-3xl overflow-hidden">
            <div className="aspect-[16/10] relative">
              {raffle.images?.[0] && (
                <img
                  src={raffle.images[0].startsWith("http") ? raffle.images[0] : `${process.env.REACT_APP_BACKEND_URL}${raffle.images[0]}`}
                  alt={raffle.title}
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute top-4 left-4 flex gap-2">
                <Badge className="bg-slate-950/80 border border-amber-500/30 text-amber-200 tracking-widest uppercase text-[10px]">
                  {raffle.category || "LUXO"}
                </Badge>
              </div>
            </div>
          </div>

          {raffle.video_url && (
            <div className="card-luxury rounded-3xl overflow-hidden mt-6">
              <div className="flex items-center gap-2 px-5 pt-4 pb-2">
                <PlayCircle className="w-5 h-5 text-emerald-300" />
                <h3 className="heading text-lg font-bold text-slate-50">Vídeo do prêmio</h3>
              </div>
              <video
                controls
                preload="metadata"
                className="w-full aspect-video bg-black"
                data-testid="raffle-video"
                src={raffle.video_url.startsWith("http") ? raffle.video_url : `${process.env.REACT_APP_BACKEND_URL}${raffle.video_url}`}
              >
                Seu navegador não suporta vídeo HTML5.
              </video>
            </div>
          )}

          {raffle.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-4">
              {raffle.images.slice(0, 4).map((im, i) => (
                <div key={i} className="aspect-square rounded-xl overflow-hidden border border-white/5">
                  <img src={im.startsWith("http") ? im : `${process.env.REACT_APP_BACKEND_URL}${im}`} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          <div className="mt-8">
            <h1 className="heading text-3xl sm:text-4xl font-extrabold text-slate-50">{raffle.title}</h1>
            <p className="mt-4 text-slate-300 leading-relaxed">{raffle.description}</p>
          </div>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="card-luxury rounded-3xl p-6">
            <div className="flex items-center justify-between text-xs uppercase tracking-widest text-slate-400">
              <span>Preço da cota</span>
              <span className="flex items-center gap-1 text-amber-200"><Calendar className="w-3.5 h-3.5" /> {fmtDate(raffle.draw_date)}</span>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="heading text-4xl font-extrabold gold-gradient-text">{brl(raffle.quota_price)}</span>
              <span className="text-sm text-slate-400">/ cota</span>
            </div>

            <div className="mt-6">
              <div className="flex justify-between text-xs text-slate-400 mb-1.5 font-mono">
                <span>{pct}% vendido</span>
                <span>{raffle.sold_quotas?.toLocaleString('pt-BR')} / {raffle.total_quotas?.toLocaleString('pt-BR')}</span>
              </div>
              <div className="progress-bar-track"><div className="progress-bar-fill" style={{ width: `${pct}%` }} /></div>
            </div>

            <div className="mt-8">
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-3">Escolha a quantidade</p>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {QUICK.map((n) => (
                  <button
                    key={n}
                    onClick={() => setQty((q) => q + n)}
                    data-testid={`quick-add-${n}`}
                    className={`rounded-full py-2.5 text-sm border transition-all font-mono ${
                      n === 100 ? "gold-btn border-transparent relative" : "border-white/10 text-slate-200 hover:border-amber-400/40 hover:text-amber-200"
                    }`}
                  >
                    +{n}
                    {n === 100 && <span className="absolute -top-2 -right-1 bg-emerald-500 text-white text-[9px] rounded-full px-1.5 py-0.5 font-bold">POP</span>}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3 justify-center">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 rounded-full border border-white/10 hover:border-amber-400/40 flex items-center justify-center text-slate-200" data-testid="qty-decr">
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  min={1}
                  max={raffle.total_quotas - raffle.sold_quotas}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1")))}
                  className="w-24 text-center bg-transparent border border-white/10 rounded-lg h-10 font-mono text-lg text-amber-200"
                  data-testid="qty-input"
                />
                <button onClick={() => setQty((q) => q + 1)} className="w-10 h-10 rounded-full border border-white/10 hover:border-amber-400/40 flex items-center justify-center text-slate-200" data-testid="qty-incr">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="mt-6 divider-gold" />

            {user && creditBalance > 0 && (
              <label className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 cursor-pointer hover:bg-emerald-500/10 transition-colors" data-testid="use-credit-toggle">
                <input
                  type="checkbox"
                  checked={useCredit}
                  onChange={(e) => setUseCredit(e.target.checked)}
                  className="mt-1 accent-emerald-500"
                />
                <div className="flex-1 text-sm">
                  <div className="text-emerald-200 font-semibold">Usar meu crédito</div>
                  <div className="text-slate-400 text-xs">Você tem <span className="text-emerald-300 font-mono">{brl(creditBalance)}</span> disponível — abate do total automaticamente.</div>
                </div>
              </label>
            )}

            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-slate-400">Total</span>
              <div className="text-right">
                {useCredit && creditBalance > 0 && (
                  <div className="text-xs text-emerald-300 line-through">{brl(total)}</div>
                )}
                <span className="heading text-2xl font-bold text-slate-50">
                  {useCredit ? brl(Math.max(0, total - creditBalance)) : brl(total)}
                </span>
              </div>
            </div>

            <Button onClick={buy} data-testid="buy-quotas-button" className="mt-6 emerald-btn w-full rounded-full h-12 text-base">
              Pagar com PIX
            </Button>
            <p className="text-center text-xs text-slate-500 mt-2">Confirmação instantânea via PIX Copia e Cola</p>
          </div>

          <div className="card-luxury rounded-3xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-amber-300" />
              <h3 className="heading text-lg font-bold text-slate-50">Prêmio ao vencedor</h3>
            </div>
            <div className="text-3xl heading font-extrabold gold-gradient-text">{brl(raffle.prize_value)}</div>
            <p className="text-sm text-slate-400 mt-2">Sorteio pela Loteria Federal em {fmtDate(raffle.draw_date)}.</p>
          </div>
        </div>
      </div>

      {/* PIX Drawer */}
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="bg-slate-950 border-t border-amber-500/20">
          <div className="max-w-lg mx-auto w-full">
            <DrawerHeader>
              <DrawerTitle className="text-slate-50 heading text-2xl flex items-center gap-2">
                <Timer className="w-5 h-5 text-emerald-400" /> Pague com PIX
              </DrawerTitle>
              <p className="text-sm text-slate-400">Escaneie o QR Code ou copie o código abaixo. O pagamento é confirmado em segundos.</p>
            </DrawerHeader>
            {order && (
              <div className="px-4 pb-4">
                <div className="glass rounded-2xl p-5 flex flex-col items-center">
                  <div className="w-56 h-56 rounded-xl bg-white p-2">
                    <img src={order.qr_code} alt="QR PIX" className="w-full h-full" data-testid="pix-qr-code" />
                  </div>
                  <div className="mt-4 text-center">
                    <div className="text-xs uppercase tracking-widest text-slate-400">Expira em</div>
                    <div className="font-mono text-2xl text-amber-200">{mins}:{secs}</div>
                  </div>
                </div>

                <div className="mt-5 rounded-xl border border-white/10 p-3">
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">PIX Copia e Cola</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs text-slate-200 break-all font-mono">{order.pix_payload}</code>
                    <Button
                      size="icon"
                      variant="outline"
                      data-testid="pix-copy-button"
                      className="border-white/10 bg-transparent"
                      onClick={() => { navigator.clipboard.writeText(order.pix_payload); toast.success("Código PIX copiado!"); }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="mt-5 rounded-xl border border-white/10 p-4 bg-slate-900/50">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Cotas</span>
                    <span className="text-slate-100 font-mono">{order.quantity}x</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-slate-400">Bilhetes</span>
                    <span className="text-amber-200 font-mono">#{order.tickets.slice(0,3).map(t => String(t).padStart(6,"0")).join(", #")}{order.tickets.length > 3 ? "..." : ""}</span>
                  </div>
                  {order.credit_used > 0 && (
                    <>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-slate-400">Subtotal</span>
                        <span className="text-slate-300 font-mono">{brl(order.total)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-emerald-300">Crédito aplicado</span>
                        <span className="text-emerald-300 font-mono">-{brl(order.credit_used)}</span>
                      </div>
                    </>
                  )}
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                    <span className="text-slate-300">A pagar via PIX</span>
                    <span className="heading text-xl font-bold gold-gradient-text">{brl(order.amount_due ?? order.total)}</span>
                  </div>
                </div>
              </div>
            )}
            <DrawerFooter>
              <div className="text-center text-sm text-slate-400 flex items-center justify-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                {checkingPayment ? "Verificando pagamento..." : "Aguardando confirmação do PIX..."}
              </div>
              <p className="text-center text-xs text-slate-500">
                Assim que o Mercado Pago confirmar seu pagamento, suas cotas aparecem no painel automaticamente.
              </p>
              <Button variant="ghost" className="text-slate-400" onClick={() => setOpen(false)}>Fechar</Button>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
